import type { AccountSettings, WhiteLabel } from '../../models/common.model';
import type { Product } from '../../shopify/shared/product.model';
import type { VariantAttribute } from '../../shopify/shared/variant-attribute.model';

export interface Membership {
  id: string;
  title: string;
  imageUrl: string;
  bannerImageUrl: string;
  header: string;
  subheader: string;
  showBanner: boolean;
  tiers: Tier[];
  selectedTier: Tier;
  selectedTerm: Term;
  contract: Contract;
  contractId: string;
  nextBillingDate: Date;
  maxMembers: number;
  currentMembers: number;
  pendingChanges: boolean;
  latestPendingChange: PendingChangeState;
  currentPerk: Product;
  whiteLabel: WhiteLabel;
  accountSettings: AccountSettings;
  createdAt: Date;
  state: MembershipState;
}

export interface Tier {
  id: string;
  title: string;
  aboutMessage: string;
  imageUrl: string;
  order: number;
  productHandle: string;
  postPurchaseUrl: string;
  legalTerms: string;
  maxMembers: number;
  currentMembers: number;
  contractState: ContractState;
  terms: Term[];
  perks: ProductShipmentPerk[];
  upcomingPerks: ProductShipmentPerk[];
  freeTier: boolean;
  walletTheme: TierWalletTheme;
}

export interface Term {
  id: string;
  price: number;
  convertedPrice: number;
  intervalType: IntervalType;
  intervalCount: number;
  renewing: boolean;
  associatedProductVariants: TermProductVariant[];
  perksDisabled: boolean;
  costComparison: CostComparison;
  sellingPlanName: string;
}

export interface CostComparison {
  annualCost: number;
  discountAmount: number;
  discountPercent: number;
}

export interface ProductShipmentPerk {
  id: string;
  productId: string;
  productVariantId: string;
  handle: string;
  type: ProductShipmentPerkType;
  attributes?: VariantAttribute[];
}

export interface Contract {
  id: string;
  fanId: string;
  shopFanId: string;
  tier: Tier;
  term: Term;
  state: ContractState;
  sourceContractId: string;
  sourcePaymentMethodId: string;
  createdAt: Date;
  startDate: Date;
  nextBillingDate: Date;
  nextShippingDate: Date;
  sourcePaymentMethodType: PaymentMethodType;
  sourcePaymentMethodBrand: string;
  sourcePaymentMethodLastDigits: string;
  sourcePaymentMethodExpiryMonth: number;
  sourcePaymentMethodExpiryYear: number;
  streetLine1: string;
  streetLine2: string;
  city: string;
  province: string;
  country: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  latestBillingAttempt: BillingAttempt;
  latestPendingChange: PendingChange;
}

export interface BillingAttempt {
  id: string;
  orderAmount: number;
  orderCurrency: string;
  orderId: string;
  orderNumber: string;
  sourceBillingAttemptId: string;
  state: string;
  errorCode: string;
  errorMessage: string;
  createdAt: Date;
}

export interface PendingChange {
  id: string;
  changeType: string;
  tier: Tier;
  term: Term;
  createdAt: Date;
  processedAt: Date;
  cancelledAt: Date;
}

export interface TermProductVariant {
  sourceProductId: string;
  sourceVariantId: string;
  handle: string;
  singleCreated: boolean;
}

export interface MembershipContractUpdateRequest {
  contractId: string;
  fanSubscriptionId: string;
  tierId: string;
  termId: string;
  changeType: ChangeType;
  immediate: boolean;
}

export interface MembershipContractUpdateCancellationRequest {
  contractId: string;
  fanSubscriptionId: string;
}

export interface MembershipContractAddressUpdateRequest {
  contractId: string;
  sourceContractId: string;
  streetLine1: string;
  streetLine2: string;
  city: string;
  province: string;
  country: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export enum ContractState {
  UPGRADE = 'UPGRADE',
  DOWNGRADE = 'DOWNGRADE',
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
}

export enum IntervalType {
  MINUTE = 'MINUTE',
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
  FOREVER = 'FOREVER',
}

export enum InteractionType {
  MANAGE = 'MANAGE',
  UPGRADE = 'UPGRADE',
  DOWNGRADE = 'DOWNGRADE',
  PENDING = 'PENDING',
  NONE = 'NONE',
}

export enum ChangeType {
  UPGRADE = 'UPGRADE',
  DOWNGRADE = 'DOWNGRADE',
  TERM_CHANGE = 'TERM_CHANGE',
  CANCEL = 'CANCEL',
}

export enum MembershipView {
  LIST = 'LIST',
  MANAGE = 'MANAGE',
}

export enum PendingChangeState {
  SUBSCRIBE = 'SUBSCRIBE',
  UPGRADE = 'UPGRADE',
  DOWNGRADE = 'DOWNGRADE',
  TERM_CHANGE = 'TERM_CHANGE',
  CANCEL = 'CANCEL',
}

export enum PaymentMethodType {
  CREDIT_CARD = 'CREDIT_CARD',
  PAYPAL = 'PAYPAL',
  SHOP_PAY = 'SHOP_PAY',
}

export enum ProductShipmentPerkType {
  AT_PURCHASE = 'AT_PURCHASE',
  AT_RENEWAL = 'AT_RENEWAL',
  RECURRING = 'RECURRING',
  MONTHLY = 'MONTHLY',
  CUSTOM = 'CUSTOM',
}

export enum MembershipState {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  SCHEDULED = 'SCHEDULED',
  ARCHIVED = 'ARCHIVED',
  PRE_SALE = 'PRE_SALE',
  UPDATED = 'UPDATED',
}

export interface MembershipJoinRequest {
  tier: Tier;
  termId?: string;
  redirectUrl?: string;
  rejectUrl?: string;
  referralLinkId?: string;
  optInToMarketingEmails?: boolean;
}

export interface ReferralLink {
  id: string;
  postSignupUrl: string;
}

export interface ConfirmTermsResult {
  termsAccepted: boolean;
}

export interface TierWalletTheme {
  id: string;
  enabled: boolean;
}

export enum WalletType {
  APPLE = 'APPLE',
  GOOGLE = 'GOOGLE',
}
